import type { ChangeEvent, ClipboardEvent, JSX, KeyboardEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { VoiceMicButton } from "./VoiceMicButton";
import { IconStop, IconPaperclip, IconTrash, IconPlus } from "../icons";
import { downscaleImage } from "../../lib/image";
import { ResourceMenu } from "../../personal-kb/ResourceMenu";
import type { MentionableAssistant } from "../../lib/api";

/** A staged image attachment: data: URL for preview, raw base64 for sending. */
export interface PendingImage {
  dataUrl: string;
  data: string;
  mimeType: string;
}

/** Wire shape sent to the backend (base64 without the data: prefix). */
export interface OutgoingImage {
  data: string;
  mimeType: string;
}

interface ComposerProps {
  onSend: (text: string, images?: OutgoingImage[], mention?: string) => void;
  onHandoff: () => void;
  onStop: () => void;
  /** True while a response is streaming. */
  streaming?: boolean;
  disabled?: boolean;
  maxChars: number;
  /** M2 @-popover feed (T4-filtered server-side); empty → no @ affordance. */
  assistants?: MentionableAssistant[];
  /** Externally staged mention (换回 / capability card); consumed once. */
  presetMention?: MentionableAssistant | null;
  onPresetConsumed?: () => void;
  /** Personal materials (P4): render the + connector menu. Absent/false = no + button. */
  personalMaterialsEnabled?: boolean;
  /** Open the full 我的资料 library manage view (查看全部 from the + menu). */
  onOpenLibrary?: () => void;
}

const MAX_IMAGES = 4;
const ACCEPTED = /^image\/(png|jpeg|jpg|gif|webp)$/i;
// Pre-decode sanity cap (raised: large screenshots are downscaled before upload,
// so we accept big files and shrink them rather than rejecting outright).
const MAX_IMAGE_BYTES = 30_000_000;

/**
 * Read a File/Blob into a PendingImage. Large screenshots are downscaled +
 * re-encoded (longest edge ~1600px, JPEG) before upload so the vision model
 * isn't fed multi-MB images (which caused request timeouts).
 */
async function readImage(file: File | Blob): Promise<PendingImage | null> {
  if (!ACCEPTED.test(file.type)) return null;
  if (file.size > MAX_IMAGE_BYTES) return null;
  // Blob (e.g. paste) → wrap as File for the shared downscaler.
  const asFile = file instanceof File ? file : new File([file], "pasted", { type: file.type });
  const enc = await downscaleImage(asFile);
  return enc ? { dataUrl: enc.dataUrl, data: enc.data, mimeType: enc.mimeType } : null;
}

export function Composer({
  onSend,
  onHandoff,
  onStop,
  streaming,
  disabled,
  maxChars,
  assistants,
  presetMention,
  onPresetConsumed,
  personalMaterialsEnabled,
  onOpenLibrary
}: ComposerProps): JSX.Element {
  const [value, setValue] = useState("");
  // P4: the + connector menu (我的资料). Anchored above the + button.
  const [resourceOpen, setResourceOpen] = useState(false);
  // M2 @-mention: the assistant explicitly picked for the NEXT send. Shown as
  // a removable chip; cleared after each send.
  const [mention, setMention] = useState<MentionableAssistant | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  useEffect(() => {
    if (presetMention !== null && presetMention !== undefined) {
      setMention(presetMention);
      onPresetConsumed?.();
    }
  }, [presetMention, onPresetConsumed]);
  const [images, setImages] = useState<PendingImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onTranscription = (text: string): void => {
    setValue((prev) => (prev.length > 0 ? `${prev} ${text}` : text));
  };

  const addFiles = async (files: FileList | File[]): Promise<void> => {
    const read = await Promise.all(Array.from(files).map(readImage));
    const next = read.filter((i): i is PendingImage => i !== null);
    if (next.length > 0) setImages((prev) => [...prev, ...next].slice(0, MAX_IMAGES));
  };

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>): void => {
    const items = Array.from(e.clipboardData.items)
      .filter((it) => it.kind === "file")
      .map((it) => it.getAsFile())
      .filter((f): f is File => f !== null);
    if (items.length > 0) {
      e.preventDefault();
      void addFiles(items);
    }
  };

  const onPickFiles = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files) void addFiles(e.target.files);
    e.target.value = "";
  };

  const removeImage = (idx: number): void => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const overLimit = value.length > maxChars;
  const warnLen = Math.floor(maxChars * 0.875);
  const showCount = value.length >= warnLen;
  // A turn can be sent with text, images, or both.
  const canSend = !disabled && !overLimit && (value.trim().length > 0 || images.length > 0);

  const submit = (): void => {
    if (!canSend) return;
    const outgoing = images.map((i) => ({ data: i.data, mimeType: i.mimeType }));
    onSend(value.trim(), outgoing.length > 0 ? outgoing : undefined, mention?.id);
    setValue("");
    setImages([]);
    setMention(null);
    setPickerOpen(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    // Ignore Enter while an IME composition is active (e.g. confirming a
    // Chinese candidate), otherwise picking a candidate submits the message.
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="composer">
      <div className={`compose ${overLimit ? "over-limit" : ""}`}>
        {images.length > 0 && (
          <div className="composer-thumbs">
            {images.map((img, i) => (
              <div className="composer-thumb" key={i}>
                <img src={img.dataUrl} alt="" />
                <button
                  type="button"
                  className="composer-thumb-del"
                  onClick={() => removeImage(i)}
                  aria-label="移除图片"
                >
                  <IconTrash size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
        <textarea
          className="compose-textarea"
          rows={1}
          placeholder={`向小环提问 — 可粘贴/上传截图，单次不超过 ${maxChars} 字`}
          value={value}
          disabled={disabled}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
        <div className="compose-row">
          <div className="compose-tools">
            {personalMaterialsEnabled === true && (
              <span className="rm-anchor">
                <button
                  className="compose-round rm-plus has-tip"
                  type="button"
                  aria-label="我的资料"
                  data-tip="我的资料"
                  data-testid="open-resource-menu"
                  aria-expanded={resourceOpen}
                  onClick={() => setResourceOpen((v) => !v)}
                >
                  <IconPlus size={16} />
                </button>
                {resourceOpen && (
                  <ResourceMenu
                    onClose={() => setResourceOpen(false)}
                    onOpenLibrary={() => onOpenLibrary?.()}
                    onAddImages={(files) => void addFiles(files)}
                  />
                )}
              </span>
            )}
            {/* Image upload: its own 📎 only when 我的资料 (+) is off — otherwise
                the + menu's 上传 absorbs images (merged, one entry). */}
            {personalMaterialsEnabled !== true && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  multiple
                  hidden
                  onChange={onPickFiles}
                />
                <button
                  className="compose-round has-tip"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled === true || images.length >= MAX_IMAGES}
                  aria-label="上传图片"
                  data-tip="上传截图"
                >
                  <IconPaperclip size={16} />
                </button>
              </>
            )}
            <VoiceMicButton onTranscription={onTranscription} />
            {(assistants?.length ?? 0) > 0 && (
              <span className="mention-wrap" style={{ position: "relative" }}>
                <button
                  type="button"
                  className="composer-icon-btn has-tip mention-btn"
                  aria-label="选择助手"
                  data-tip="指定这条消息问哪个助手"
                  onClick={() => setPickerOpen((v) => !v)}
                >
                  @
                </button>
                {pickerOpen && (
                  <div className="mention-popover" data-testid="mention-popover" role="listbox">
                    {assistants?.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        role="option"
                        aria-selected={mention?.id === a.id}
                        className="mention-option"
                        onClick={() => {
                          setMention(a);
                          setPickerOpen(false);
                        }}
                      >
                        <strong>{a.name}</strong>
                        {a.tagline !== undefined && (
                          <span className="mention-tagline"> {a.tagline}</span>
                        )}
                        {a.isDefault && <span className="mention-tagline">（默认）</span>}
                      </button>
                    ))}
                  </div>
                )}
              </span>
            )}
            {mention !== null && (
              <span className="mention-chip" data-testid="mention-chip">
                @{mention.name}
                <button type="button" aria-label="取消指定" onClick={() => setMention(null)}>
                  ×
                </button>
              </span>
            )}
            {showCount && (
              <span className={`composer-counter ${overLimit ? "over" : "warn"}`}>
                {value.length}/{maxChars}
              </span>
            )}
          </div>
          <div className="compose-actions">
            <button className="handoff-btn" type="button" onClick={onHandoff}>
              转人工
            </button>
            {streaming === true ? (
              <button
                className="compose-send stop has-tip"
                type="button"
                onClick={onStop}
                aria-label="停止"
                data-tip="停止生成"
              >
                <IconStop size={14} />
              </button>
            ) : (
              <button
                className="compose-send has-tip"
                type="button"
                onClick={submit}
                disabled={!canSend}
                aria-label="发送"
                data-tip="发送 · Enter"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
